import { ReactNode, useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Portal, Tooltip } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import {
  DEFAULT_COVER_LETTER_SECTIONS,
  defaultCoverLetterLayout,
  flattenCoverLetterLayoutItems,
  insertBeforeClosingInPlace,
} from "@reactive-resume/schema";
import { ArrowCounterClockwise, DotsSixVertical, Layout } from "@phosphor-icons/react";

import { useCoverLetterStore } from "@/client/stores/cover-letter";
import { CLCollapsibleSection } from "../../../CollapsibleSection";

const sectionNameMap: Record<string, string> = {
  date: "Date",
  recipient: "Recipient",
  reference: "Reference / Subject",
  letter: "Letter Content",
  signoff: "Sign-off",
  enclosures: "Enclosures",
};

type SortableSectionProps = {
  id: string;
};

const SortableSection = ({ id }: SortableSectionProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transition,
    opacity: isDragging ? 0.35 : 1,
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <Section id={id} isDragging={isDragging} />
    </div>
  );
};

type SectionProps = {
  id: string;
  isDragging?: boolean;
};

const Section = ({ id, isDragging = false }: SectionProps) => {
  const name = useCoverLetterStore((state) => {
    if (id.startsWith("custom.")) {
      const customId = id.split("custom.")[1];
      const customGroup = state.coverLetter.data?.custom?.[customId];
      if (!customGroup) return "Custom Section";
      return customGroup.name && customGroup.name.trim() ? customGroup.name : "Custom Section";
    }
    return sectionNameMap[id] || id;
  });

  const isCustom = id.startsWith("custom.");

  return (
    <div
      className={cn(
        "flex select-none items-center gap-x-2.5 rounded-md border border-neutral-200 bg-white px-3 py-2.5 shadow-sm transition-all hover:border-neutral-300 hover:shadow-sm",
        isDragging && "border-primary/50 bg-primary/5 shadow-md",
      )}
    >
      <DotsSixVertical size={16} weight="bold" className="cursor-grab text-neutral-400 active:cursor-grabbing" />
      <span className="flex-1 truncate text-xs font-semibold text-neutral-800">{name}</span>
      {isCustom && (
        <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500">
          Custom
        </span>
      )}
    </div>
  );
};

export const CLLayoutSection = () => {
  const setValue = useCoverLetterStore((state) => state.setValue);
  const layout = useCoverLetterStore(
    (state) => state.coverLetter?.data?.metadata?.layout || defaultCoverLetterLayout,
  );
  const customSections = useCoverLetterStore(
    (state) => state.coverLetter?.data?.custom || {},
  );

  const [activeId, setActiveId] = useState<string | null>(null);

  const items = useMemo(
    () => flattenCoverLetterLayoutItems(layout, customSections),
    [layout, customSections],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id as string);
  };

  const onDragCancel = () => {
    setActiveId(null);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    const oldIndex = items.indexOf(active.id as string);
    const newIndex = items.indexOf(over.id as string);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newItems = arrayMove(items, oldIndex, newIndex);
      setValue("metadata.layout", [[newItems, []]]);
    }

    setActiveId(null);
  };

  const onResetLayout = () => {
    const resetItems = [...DEFAULT_COVER_LETTER_SECTIONS];
    for (const id of Object.keys(customSections)) {
      insertBeforeClosingInPlace(resetItems, `custom.${id}`);
    }
    setValue("metadata.layout", [[resetItems, []]]);
  };

  const headerHTML: ReactNode = (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-x-4">
        <Layout size={18} />
        <h2 className="line-clamp-1 text-base font-bold text-gray-800 lg:text-lg">Layout</h2>
      </div>

      <Tooltip content="Reset Layout">
        <Button size="icon" variant="ghost" onClick={onResetLayout}>
          <ArrowCounterClockwise size={16} />
        </Button>
      </Tooltip>
    </div>
  );

  const contentHTML: ReactNode = (
    <div className="space-y-3">
      <p className="text-xs text-neutral-500">
        Drag and drop sections to rearrange the order in your cover letter.
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragCancel={onDragCancel}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map((sectionId) => (
              <SortableSection key={sectionId} id={sectionId} />
            ))}
          </div>
        </SortableContext>

        <Portal>
          <DragOverlay>{activeId && <Section isDragging id={activeId} />}</DragOverlay>
        </Portal>
      </DndContext>
    </div>
  );

  return <CLCollapsibleSection id="cl-layout" header={headerHTML} content={contentHTML} />;
};
